import { CheckCircleIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { getChainName } from '@revoke.cash/core/chains';
import type { ExploiStatus as ExploitStatusString } from '@revoke.cash/core/exploits';
import type { AddressOnChain } from '@revoke.cash/core/types';
import { shortenAddress } from '@revoke.cash/core/utils/formatting';
import Button from 'components/common/Button';
import { useTranslations } from 'next-intl';
import type { Address } from 'viem';

interface Props {
  address: Address;
  chainId: number;
  status: ExploitStatusString;
  matchedSpenders: AddressOnChain[];
}

const ExploitStatus = ({ address, chainId, status, matchedSpenders }: Props) => {
  const t = useTranslations();

  const chainName = getChainName(chainId);

  const icons = {
    affected: <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />,
    safe: <CheckCircleIcon className="h-12 w-12 text-green-500" />,
    previously_affected: <ShieldCheckIcon className="h-12 w-12 text-yellow-500" />,
  };

  const spenderSearch = matchedSpenders.map(({ address }) => address).join(',');
  const baseHref = `/address/${address}?chainId=${chainId}`;
  const href = status === 'affected' ? `${baseHref}&spenderSearch=${spenderSearch}` : baseHref;

  return (
    <div className={'rounded-xl border border-zinc-200 dark:border-zinc-800 p-4'}>
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="shrink-0">{icons[status]}</div>
          <div className="flex flex-col gap-2 ml-2">
            <h3 className="exploit-checker-status">{t(`exploits.subpages.result.${status}.title`)}</h3>
            <p className="text-sm">
              {t(`exploits.subpages.result.${status}.description`, {
                address: shortenAddress(address)!,
                chainName,
              })}
            </p>
          </div>
        </div>
        <Button className="shrink-0 w-full sm:w-fit" href={href} size="md" style="secondary" router>
          {t(`exploits.subpages.result.${status}.button`)}
        </Button>
      </div>
    </div>
  );
};

export default ExploitStatus;
